import type { ThemedObjectStyleFunction } from 'domain/styled'
import type { FC } from 'react'
import type { Facet } from 'services/Search/utils/filters'

import { FilterOutline16 } from '@sok/design-system'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useActiveFilters } from 'services/Search/hooks/use-active-filters'
import styled from 'styled-components'

import { FilterCount } from './filters/FilterCount'
import { FilterDrawer } from './filters/FilterDrawer'

interface Props {
  className?: string
  loading: boolean
  facets?: Facet[] | null
}

const FiltersComponent: FC<Props> = ({ className, loading, facets }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const handleOpen = useCallback(() => void setIsOpen(true), [])
  const handleClose = useCallback(() => void setIsOpen(false), [])

  const activeFilters = useActiveFilters()

  const activeFilterTotalCount = useMemo(
    () => activeFilters.flatMap(({ stringValue }) => stringValue).length,
    [activeFilters],
  )

  return (
    <>
      <button
        className={className}
        data-test-id="search-filters-button"
        disabled={!facets?.length}
        onClick={handleOpen}
      >
        <span>{t`Filter`}</span>
        <FilterCount count={activeFilterTotalCount} />
        <FilterOutline16 />
      </button>
      <FilterDrawer facets={facets} loading={loading} open={isOpen} onClose={handleClose} />
    </>
  )
}

FiltersComponent.displayName = 'Filters'

export const filterStyles: ThemedObjectStyleFunction = ({ theme }) => ({
  ...theme.variants.body2.regular,
  alignItems: 'center',
  background: 'none',
  border: 'none',
  color: 'inherit',
  cursor: 'pointer',
  display: 'flex',
  padding: theme.spacings.xSmall,

  '> *:not(:first-child)': {
    marginLeft: theme.spacings.xSmall,
  },

  '&:disabled': {
    cursor: 'not-allowed',
    opacity: '0.5',
  },

  '&:active': {
    color: theme.color.action,

    [FilterCount]: {
      backgroundColor: theme.color.action,
    },
  },

  '&:focus, &:hover:not(:disabled)': {
    color: theme.color.highlightHoverColor,
    [FilterCount]: {
      backgroundColor: theme.color.highlightHoverColor,
    },
  },
})

export const Filters = styled(FiltersComponent)(filterStyles)
